import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ServiceStatus } from '@/models/enums/service';
import { BasicInfoModel, TargetDashboardModel } from '@/models/classes/report/dashboard_target';
import { OptionModel } from '@/models/classes/report/option';
import Service from '@/services';
const service = new Service.ReportDashboardTargetService();

export interface DashboardTargetState {
  filter: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: OptionModel[];
  };
  info: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: BasicInfoModel | null;
  };
  target: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: TargetDashboardModel[];
  };
}

const initialState: DashboardTargetState = {
  filter: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: []
  },
  info: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: null
  },
  target: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: []
  }
};

const dashboardTargetSlice = createSlice({
  name: 'reportTarget',
  initialState,
  reducers: {
    reset: () => initialState,
    patch: (state, action) => ({
      ...state,
      ...action.payload
    }),
  },
  extraReducers: (builder) => {
     // Filter
    builder.addCase(onFetchFilter.pending, (state) => {
      state.filter.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchFilter.fulfilled, (state, action) => {
      state.filter.network.code = ServiceStatus.succeeded;
      state.filter.data = action.payload;
    });
    builder.addCase(onFetchFilter.rejected, (state, action) => {
      state.filter.network.code = ServiceStatus.failed;
      state.filter.network.error = action.payload as string || 'Failed to fetch filter data';
    });

    // Info
    builder.addCase(onFetchInfo.pending, (state) => {
      state.info.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchInfo.fulfilled, (state, action) => {
      state.info.network.code = ServiceStatus.succeeded;
      state.info.data = action.payload;
    });
    builder.addCase(onFetchInfo.rejected, (state, action) => {
      state.info.network.code = ServiceStatus.failed;
      state.info.network.error = action.payload as string || 'Failed to fetch info';
    });

    // Target Data
    builder.addCase(onFetchTarget.pending, (state) => {
      state.target.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchTarget.fulfilled, (state, action) => {
      state.target.network.code = ServiceStatus.succeeded;
      state.target.data = action.payload;
    });
    builder.addCase(onFetchTarget.rejected, (state, action) => {
      state.target.network.code = ServiceStatus.failed;
      state.target.network.error = action.payload as string || 'Failed to fetch target data';
    });
  },
});


// Thunks
export const onFetchFilter = createAsyncThunk(
  'dashboard/target/filter',
  async (_, { rejectWithValue }) => {
    try {
      const response = await service.getEmployeeFilter();
      return response || [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const onFetchInfo = createAsyncThunk(
  'dashboard/target/info',
  async (_, { rejectWithValue }) => {
    try {
      const response = await service.getBasicInfo();
      return response || null;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const onFetchTarget = createAsyncThunk(
  'dashboard/target/data',
  async (params: { employeeId?: string; searchText?: string } = {}, { rejectWithValue }) => {
    try {
      const response = await service.getTargetData(params);
      return response || [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const { reset, patch } = dashboardTargetSlice.actions;
export default dashboardTargetSlice.reducer;